import { Component, OnInit, ViewChild } from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import dayjs from "dayjs";
import { catchError, filter, throwError } from 'rxjs';
import { CreateEditSeasonComponent } from 'src/app/dialogs/season-manager-dialogs/create-edit-season/create-edit-season.component';
import { EndSeasonComponent } from 'src/app/dialogs/season-manager-dialogs/end-season/end-season.component';
import { HelperService } from 'src/app/helper.service';
import { FieldAdminSeasonFarmFilterModel } from 'src/app/models/season-models/field-admin-season-farm-filter-model';
import { FieldAdminSeasonFieldsModel } from 'src/app/models/season-models/field-admin-season-fields-model';
import { NavBarComponent } from 'src/app/nav-bar/nav-bar.component';
import { BaseService } from 'src/app/services/base.service';
import { SeasonManagerService } from 'src/app/services/season-manager.service';
import { EditFieldLinkSeasonComponent } from '../edit-field-link-season/edit-field-link-season.component';
import { SeasonSharedDataService } from 'src/app/services/season-shared-data.service';
import { TenantService } from 'src/app/services/tenant.service';

@Component({
    selector: 'app-season-field-admin-fields',
    templateUrl: './season-field-admin-fields.component.html',
    styleUrls: ['./season-field-admin-fields.component.css'],
    standalone: false
})
export class SeasonFieldAdminFieldsComponent extends BaseService implements OnInit {
  fieldAdminFieldsForm: FormGroup;

  currentPage: number = 0;
  pageSize: number = 25;
  totalElements: number = 0;

  tenantId: string = null;
  farmId: number = null;

  protected readonly dayjs = dayjs;
  dateStringFormatting = 'YYYY-MM-DD';
  todayDate = dayjs();

  placeholder: string = "-";
  searchValue: string = null;

  isFilterBtnVisible: boolean = false;
  isNoItemsFound: boolean = false;
  activeSeasonYES: string = 'Yes';
  activeSeasonNO: string = 'No';

  defaultValue = null;

  activeSeasonStatus: any[] = [];
  filterStatus: any = null;
  farmFilter: any = null;

  // Custom dropdown states
  showSeasonStatusMenu: boolean = false;
  showFarmMenu: boolean = false;
  showPageSizeMenu: boolean = false;

  // Side panel state
  sidePanelOpen: boolean = false;
  selectedFieldId: number = null;
  selectedFieldName: string = '';
  selectedFieldSeasonName: string = '';
  selectedSeasonData: any = null;
  panelTab: 'field' | 'crop' | 'cost' = 'field';

  @ViewChild(EditFieldLinkSeasonComponent) editFieldComponent: EditFieldLinkSeasonComponent;

  displayedColumns: string[] = ['farmName', 'fieldName', 'sizeHA', 'inSeason', 'seasonName', ' '];
  dataSource = new MatTableDataSource<FieldAdminSeasonFieldsModel>([]);

  farmList: FieldAdminSeasonFarmFilterModel[] = [];

  constructor(
    public helper: HelperService,
    private router: Router,
    public nav: NavBarComponent,
    private seasonManagerService: SeasonManagerService,
    private seasonSharedDataService: SeasonSharedDataService,
    public endSeasonDialog: MatDialog,
    public createSeasonDialog: MatDialog,
    private tenantService: TenantService,
    private snackBar: MatSnackBar,
  ) {
    super();
    this.fieldAdminFieldsForm = new FormGroup({
      activeSeason: new FormControl(null),
      farm: new FormControl(null)
    });
  }

  ngOnInit(): void {
    this.tenantService.currenttenantId.pipe(
      filter(id => id != null)
    ).subscribe((tenantId) => {
      this.tenantId = tenantId;
      this.populateFieldList();
      this.populateFarmsDropdownDetails();
    });

    this.activeSeasonStatus = [
      { id: 1, description: 'All', value: null },
      { id: 2, description: 'Active', value: true },
      { id: 3, description: 'Inactive', value: false }
    ];

    this.filterStatus = this.activeSeasonStatus[0];
  }

  get lastPage(): number {
    return Math.max(0, Math.ceil(this.totalElements / this.pageSize) - 1);
  }

  min(a: number, b: number): number {
    return Math.min(a, b);
  }

  hideShowFilterReset() {
    this.isFilterBtnVisible = !!(this.searchValue || (this.filterStatus && this.filterStatus.description !== 'All') || this.farmFilter);
  }

  setActiveFarmFilter(farm: any) {
    this.farmFilter = farm;
    this.fieldAdminFieldsForm.controls.farm.setValue(farm);
    this.populateFieldList();
  }

  setActiveSeasonStatus(status: any) {
    this.filterStatus = status;
    this.fieldAdminFieldsForm.controls.activeSeason.setValue(status);
    this.populateFieldList();
  }

  populateFieldList() {
    this.nav.toggleLoading(true);
    let tenantId = Number(this.tenantId || sessionStorage.getItem("selectedTenant"));
    if (!tenantId || isNaN(tenantId)) {
      this.nav.toggleLoading(false);
      return;
    }
    let date = this.dayjs(this.todayDate).format('YYYY-MM-DDTHH:mm:ss.SSSZ');

    let farm = this.fieldAdminFieldsForm.controls.farm.value;
    let status = this.fieldAdminFieldsForm.controls.activeSeason.value;

    this.seasonManagerService.getPaginatedFieldAdminFields(
      tenantId,
      this.currentPage,
      this.pageSize,
      date,
      farm ? farm.id : null,
      this.searchValue ? this.searchValue : null,
      status ? this.filterStatus.value : null,
    ).pipe(
      catchError(error => {
        this.nav.toggleLoading(false);
        return throwError(() => error);
      })
    ).subscribe((response) => {
      if (response.content.length !== 0) {
        this.isNoItemsFound = false;
        this.dataSource.data = response.content;
      } else {
        this.dataSource.data = [];
        this.isNoItemsFound = true;
      }
      this.hideShowFilterReset();
      this.totalElements = response.totalElements;
      this.nav.toggleLoading(false);
    });
  }

  populateFarmsDropdownDetails() {
    let tenantId = Number(this.tenantId || sessionStorage.getItem("selectedTenant"));
    if (!tenantId || isNaN(tenantId)) return;
    this.seasonManagerService.getSeasonFarmFilter(tenantId).pipe(
      catchError(error => {
        return throwError(() => error);
      })
    ).subscribe((response) => {
      this.farmList = response ?? [];
    });
  }

  // Custom pagination
  goToPage(page: number) {
    if (page < 0 || page > this.lastPage) return;
    this.currentPage = page;
    this.populateFieldList();
  }

  onPageSizeChange(size: number) {
    this.pageSize = size;
    this.currentPage = 0;
    this.showPageSizeMenu = false;
    this.populateFieldList();
  }

  /**
   * Reads the child's validity directly via the ViewChild, so the panel's own
   * controls can be blocked while the open field's cultivar size exceeds its
   * total.
   */
  get cultivarSizeInvalid(): boolean {
    return !!this.editFieldComponent?.cultivarSizeInvalid;
  }

  viewField(field: any) {
    // Switching fields auto-saves the current one, so block it while the
    // cultivar size is invalid. Re-selecting the open field is harmless.
    if (this.cultivarSizeInvalid && this.sidePanelOpen && this.selectedFieldId !== field.fieldId) {
      return;
    }

    // Auto-save current field before switching
    if (this.sidePanelOpen && this.editFieldComponent) {
      this.editFieldComponent.saveSeasonDetails();
    }
    this.selectedFieldId = field.fieldId;
    this.selectedFieldName = field.fieldName || 'Field Details';
    this.selectedFieldSeasonName = field.seasonName || '';
    this.panelTab = 'field';
    this.sidePanelOpen = true;
  }

  closeSidePanel() {
    // Closing stays available as a way out, but skips the save when the
    // cultivar size is invalid - otherwise closing would persist the very
    // value every other control is blocking.
    if (this.editFieldComponent && !this.cultivarSizeInvalid) {
      this.editFieldComponent.saveSeasonDetails();
    }
    this.sidePanelOpen = false;
    this.selectedFieldId = null;
    this.selectedSeasonData = null;
  }

  onSeasonDataLoaded(season: any) {
    if(season?.status !== 'Ended') {
      this.selectedSeasonData = season;
    }
  }

  saveFieldFromPanel() {
    if (this.editFieldComponent) {
      this.nav.toggleLoading(true);
      this.editFieldComponent.saveSeasonDetails();
      // Give the save calls time to complete, then show confirmation
      setTimeout(() => {
        this.nav.toggleLoading(false);
        this.snackBar.open('Field details updated', 'close', { duration: 3000 });
      }, 1500);
    }
  }

  onFieldNameUpdated(name: string) {
    // Update panel header
    this.selectedFieldName = name;
    // Update the field list table row
    const row = this.dataSource.data.find(f => f.fieldId === this.selectedFieldId);
    if (row) {
      row.fieldName = name;
    }
  }

  search(searchValue: string) {
    this.searchValue = searchValue.trim().toLocaleLowerCase();
    setTimeout(() => {
      this.populateFieldList();
    }, 1400);
  }

  resetFilter() {
    this.router.navigateByUrl("/season-manager/seasons", { skipLocationChange: true }).then(() => {
      this.router.navigate(["/season-manager/fields"]);
    });
  }
}
